import { useCallback } from 'react';
import {
  recalculateShiftHours,
  recalculateDayHours,
  recalculatePayoutHours,
  calculateTimeMidpoint,
} from '../../utils/timekeeping/shiftCalculations';

/**
 * Generic hook for managing shift operations
 * Handles adding, removing, and updating shifts with automatic recalculation
 * 
 * @param {Function} setShiftsData - State setter for weekly hours data
 * @param {Object} config - Configuration object with field names and recalculation functions
 * @param {Object} config.fieldNames - Field name mappings
 * @param {string} config.fieldNames.timeStart - Field name for start time (e.g., 'startTimePayout' or 'startTime')
 * @param {string} config.fieldNames.timeEnd - Field name for end time (e.g., 'finishTimePayout' or 'finishTime')
 * @param {string} config.fieldNames.hoursRegular - Field name for regular hours (e.g., 'regularTimePayout' or 'regularTime')
 * @param {string} config.fieldNames.hoursOvertime - Field name for overtime hours (e.g., 'overtimePayout' or 'overtime')
 * @param {string} config.fieldNames.hoursDoubleTime - Field name for double time hours (e.g., 'doubleTimePayout' or 'doubleTime')
 * @param {Function} config.recalculateShiftHoursFn - Function to recalculate shift hours
 * @param {Function} config.recalculateDayHoursFn - Function to recalculate day totals
 * @param {Function} config.recalculateAllHoursFn - Function to recalculate all hours in weekly data
 * @param {Object} config.defaultShift - Default shift object for new shifts (optional, for add functionality)
 * @returns {Object} - Shift management functions
 */
export const useShiftManagement = (
  setShiftsData,
  config = {}
) => {
  // Default configuration for manager (payout) fields
  const defaultConfig = {
    fieldNames: {
      timeStart: 'startTimePayout',
      timeEnd: 'finishTimePayout',
      hoursRegular: 'regularTimePayout',
      hoursOvertime: 'overtimePayout',
      hoursDoubleTime: 'doubleTimePayout',
    },
    recalculateShiftHoursFn: recalculateShiftHours,
    recalculateDayHoursFn: recalculateDayHours,
    recalculateAllHoursFn: recalculatePayoutHours,
    defaultShift: {
      id: null,
      shiftIndex: 0,
      job: null,
      jobPayout: null,
      workScope: null,
      workDescription: null,
      breaksCompleted: true,
      startTime: '07:00',
      finishTime: '15:00',
      startTimePayout: '07:00',
      finishTimePayout: '15:00',
      regularTime: 0,
      overtime: 0,
      doubleTime: 0,
      regularTimePayout: 0,
      overtimePayout: 0,
      doubleTimePayout: 0,
      hoursOverrides: {},
      missingLogs: false,
    },
  };

  const finalConfig = { ...defaultConfig, ...config };
  const {
    fieldNames,
    recalculateShiftHoursFn,
    recalculateDayHoursFn,
    recalculateAllHoursFn,
    defaultShift,
  } = finalConfig;

  /**
   * Update shifts data handler that triggers recalculation
   * @param {Function} updateFunction - Function that receives prevData and returns updated data
   */
  const handleShiftsUpdate = useCallback(
    (updateFunction) => {
      setShiftsData((prevData) => {
        const updatedData = updateFunction(prevData);
        return recalculateAllHoursFn(updatedData);
      });
    },
    [setShiftsData, recalculateAllHoursFn]
  );

  /**
   * Adds a new shift to a specific day by duplicating an existing shift and splitting the times
   * @param {number} dayIndex - Index of the day in shiftsData array
   * @param {number} insertAfterShiftIndex - Index of shift to insert after (use -1 to insert at start)
   */
  const addShiftToDay = useCallback(
    (dayIndex, insertAfterShiftIndex) => {
      if (!defaultShift) {
        // Add functionality not available (e.g., for worker timesheet)
        return;
      }

      setShiftsData((prevData) => {
        const newData = [...prevData];
        const day = { ...newData[dayIndex] };
        const shifts = day.shifts || [];

        // Handle empty day: Create default shift
        if (shifts.length === 0) {
          const newShift = { ...defaultShift, isPayoutCreatedShift: true };

          // Recalculate hours for the new shift
          const recalculatedShifts = recalculateShiftHoursFn([newShift], day.date);
          day.shifts = recalculatedShifts;

          // Recalculate day totals
          const updatedDay = recalculateDayHoursFn(day);
          newData[dayIndex] = updatedDay;

          return newData;
        }

        // Existing shifts: Use duplication logic
        const shiftsArray = [...shifts];

        // Get the shift to duplicate (use the shift we're inserting after, or first shift if -1)
        const shiftToDuplicate =
          insertAfterShiftIndex >= 0
            ? shiftsArray[insertAfterShiftIndex]
            : shiftsArray[0];

        if (!shiftToDuplicate) {
          // Safety check: if no shifts exist, return unchanged
          return prevData;
        }

        // Get original times using field name mapping
        const originalStartTime = shiftToDuplicate[fieldNames.timeStart];
        const originalEndTime = shiftToDuplicate[fieldNames.timeEnd];

        // Calculate midpoint between start and end times
        const midpointTime = calculateTimeMidpoint(
          originalStartTime,
          originalEndTime
        );

        // Create a copy of the shift array to modify
        const updatedShifts = [...shiftsArray];

        // Update the original shift: end time becomes midpoint
        const originalShiftIndex =
          insertAfterShiftIndex >= 0 ? insertAfterShiftIndex : 0;
        updatedShifts[originalShiftIndex] = {
          ...updatedShifts[originalShiftIndex],
          [fieldNames.timeEnd]: midpointTime,
        };

        // Duplicate the shift and modify for new shift
        const newShift = {
          ...shiftToDuplicate,
          // Generate temporary ID that backend can recognize as new
          id: null,
          isPayoutCreatedShift: true,
          // Update shiftIndex to be the next index
          shiftIndex: shiftsArray.length,
          // Set times: start at midpoint, end at original end time
          [fieldNames.timeStart]: midpointTime,
          [fieldNames.timeEnd]: originalEndTime,
          // Reset hours to 0 (will be recalculated)
          [fieldNames.hoursRegular]: 0,
          [fieldNames.hoursOvertime]: 0,
          [fieldNames.hoursDoubleTime]: 0,
          regularTime: 0,
          overtime: 0,
          doubleTime: 0,
          regularTimeHoursLog: 0,
          overtimeHoursLog: 0,
          doubleTimeHoursLog: 0,
          startTimeHoursLog: null,
          finishTimeHoursLog: null,
          jobHoursLog: null,
          hoursOverrides: {},
          // Keep all other fields from the duplicated shift
        };

        // Insert the new shift after the specified index
        const insertIndex = insertAfterShiftIndex + 1;
        updatedShifts.splice(insertIndex, 0, newShift);

        // Recalculate hours for all shifts in the day
        // This will automatically distribute RT/OT across all shifts
        const recalculatedShifts = recalculateShiftHoursFn(updatedShifts, day.date);
        day.shifts = recalculatedShifts;

        // Recalculate day totals
        const updatedDay = recalculateDayHoursFn(day);
        newData[dayIndex] = updatedDay;

        return newData;
      });
    },
    [setShiftsData, fieldNames, recalculateShiftHoursFn, recalculateDayHoursFn, defaultShift]
  );

  /**
   * Removes a shift from a specific day
   * @param {number} dayIndex - Index of the day in shiftsData array
   * @param {number} shiftIndexToRemove - Index of the shift to remove
   */
  const removeShiftFromDay = useCallback(
    (dayIndex, shiftIndexToRemove) => {
      setShiftsData((prevData) => {
        const newData = [...prevData];
        const day = { ...newData[dayIndex] };
        const shifts = [...day.shifts];

        // Allow removing the last shift (will result in empty shifts array)
        if (shifts.length === 0) {
          return prevData;
        }

        // Safety check: shiftIndex must be valid
        if (shiftIndexToRemove < 0 || shiftIndexToRemove >= shifts.length) {
          return prevData;
        }

        // Remove the shift from the array
        const updatedShifts = [...shifts];
        updatedShifts.splice(shiftIndexToRemove, 1);

        // If no shifts remain, set to empty array
        if (updatedShifts.length === 0) {
          day.shifts = [];
        } else {
          // Update shiftIndex values for remaining shifts
          updatedShifts.forEach((shift, index) => {
            shift.shiftIndex = index;
          });

          // Recalculate hours for all remaining shifts in the day
          const recalculatedShifts = recalculateShiftHoursFn(updatedShifts, day.date);
          day.shifts = recalculatedShifts;
        }

        // Recalculate day totals
        const updatedDay = recalculateDayHoursFn(day);
        newData[dayIndex] = updatedDay;

        return newData;
      });
    },
    [setShiftsData, recalculateShiftHoursFn, recalculateDayHoursFn]
  );

  return {
    handleShiftsUpdate,
    addShiftToDay: defaultShift ? addShiftToDay : undefined,
    removeShiftFromDay,
  };
};
